import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { publicAPI } from '../lib/api';
import { Menu, X, Wrench, ChevronDown, Phone } from 'lucide-react';
import { Button } from './ui/button';

export default function PublicLayout({ children }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [footerContent, setFooterContent] = useState({});
  const [headerContent, setHeaderContent] = useState({});
  const [siteSettings, setSiteSettings] = useState({});
  const [branding, setBranding] = useState({ favicon: {}, logo: '', site_title: '', site_description: '' });
  const location = useLocation();
  const BACKEND = '';

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  useEffect(() => {
    publicAPI.getContent('footer').then(r => setFooterContent(r.data)).catch(() => {});
    publicAPI.getContent('header').then(r => setHeaderContent(r.data)).catch(() => {});
    publicAPI.getPublicSettings().then(r => setSiteSettings(r.data)).catch(() => {});
    publicAPI.getPublicBranding().then(r => setBranding(r.data)).catch(() => {});
  }, []);

  const navLinks = [
    { href: '/', label: 'Ana Sayfa' },
    { href: '/hizmetler', label: 'Hizmetler' },
    { href: '/sehirler', label: 'Sehirler' },
    { href: '/sanayi-rehberi', label: 'Sanayi Rehberi' },
    { href: '/blog', label: 'Blog' },
    { href: '/hakkimizda', label: 'Hakkimizda' },
    { href: '/iletisim', label: 'Iletisim' },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Centralized SEO Base Tags + Analytics Injection */}
      <Helmet>
        {/* Default SEO Meta Tags (overridden by individual pages via react-helmet-async) */}
        <meta name="description" content={branding.site_description || "CNC torna, lazer kesim, kalip uretimi ve endustriyel uretim hizmetleri icin Turkiye genelinde hizli teklif alin. UretimNoktasi.com ile uretim projelerinize dogru firmayi bulun."} />
        <link rel="canonical" href="https://uretimnoktasi.com" />
        <meta property="og:title" content={branding.site_title || "Uretim Noktasi - Endustriyel Uretim Platformu"} />
        <meta property="og:description" content={branding.site_description || "Turkiye genelinde CNC torna, lazer kesim ve uretim hizmetleri icin teklif alin."} />
        <meta property="og:url" content="https://uretimnoktasi.com" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={branding.site_title || "Uretim Noktasi"} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={branding.site_title || "Uretim Noktasi - Endustriyel Uretim Platformu"} />
        <meta name="twitter:description" content={branding.site_description || "Turkiye genelinde CNC torna, lazer kesim ve uretim hizmetleri icin teklif alin."} />

        {/* Dynamic Favicon from Branding */}
        {branding.favicon?.['32'] ? (
          <link rel="icon" sizes="32x32" href={`${BACKEND}${branding.favicon['32']}`} type="image/png" />
        ) : (
          <link rel="icon" href="/favicon.svg" type="image/svg+xml" />
        )}
        {branding.favicon?.['16'] && <link rel="icon" sizes="16x16" href={`${BACKEND}${branding.favicon['16']}`} type="image/png" />}
        {branding.favicon?.['48'] && <link rel="icon" sizes="48x48" href={`${BACKEND}${branding.favicon['48']}`} type="image/png" />}
        {branding.favicon?.['180'] && <link rel="apple-touch-icon" sizes="180x180" href={`${BACKEND}${branding.favicon['180']}`} />}
        {branding.favicon?.['192'] && <link rel="icon" sizes="192x192" href={`${BACKEND}${branding.favicon['192']}`} type="image/png" />}
        {branding.favicon?.['512'] && <link rel="icon" sizes="512x512" href={`${BACKEND}${branding.favicon['512']}`} type="image/png" />}

        {/* Google Search Console Verification */}
        {siteSettings.search_console_code && (
          <meta name="google-site-verification" content={siteSettings.search_console_code} />
        )}

        {/* Google Analytics 4 */}
        {siteSettings.ga_measurement_id && (
          <script async src={`https://www.googletagmanager.com/gtag/js?id=${siteSettings.ga_measurement_id}`}></script>
        )}
        {siteSettings.ga_measurement_id && (
          <script>{`window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${siteSettings.ga_measurement_id}');`}</script>
        )}

        {/* Google Tag Manager */}
        {siteSettings.gtm_container_id && (
          <script>{`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${siteSettings.gtm_container_id}');`}</script>
        )}

        {/* Meta Pixel */}
        {siteSettings.meta_pixel_id && (
          <script>{`!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${siteSettings.meta_pixel_id}');fbq('track','PageView');`}</script>
        )}
      </Helmet>

      {/* Custom Head Scripts (raw HTML injection) */}
      {siteSettings.head_scripts && (
        <div dangerouslySetInnerHTML={{ __html: `<!-- Custom Head Scripts -->${siteSettings.head_scripts}` }} style={{ display: 'none' }} />
      )}

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b" data-testid="public-header">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="logo">
              {branding.logo ? (
                <img src={`${BACKEND}${branding.logo}`} alt={branding.site_title || 'Uretim Noktasi'} className="h-8 w-auto object-contain" />
              ) : (
                <Wrench className="h-6 w-6 text-[hsl(221,83%,22%)]" />
              )}
              <span className="text-lg font-semibold text-[hsl(221,83%,22%)]">
                {branding.site_title || footerContent.company_name || 'Uretim Noktasi'}
              </span>
            </Link>

            <nav className="hidden md:flex items-center gap-6" data-testid="desktop-nav">
              {navLinks.map(link => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`text-sm font-medium transition-colors hover:text-[hsl(221,83%,22%)] ${
                    location.pathname === link.href ? 'text-[hsl(221,83%,22%)]' : 'text-muted-foreground'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            <div className="flex items-center gap-3">
              <a href={headerContent.cta_link || 'tel:+902120000000'} className="hidden sm:block">
                <Button size="sm" data-testid="header-cta">
                  <Phone className="h-4 w-4 mr-1.5" />
                  {headerContent.cta_text || 'Bizi Arayin'}
                </Button>
              </a>
              <button
                className="md:hidden p-2"
                onClick={() => setMenuOpen(!menuOpen)}
                data-testid="mobile-menu-toggle"
              >
                {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {menuOpen && (
            <div className="md:hidden border-t py-4 space-y-3" data-testid="mobile-nav">
              {navLinks.map(link => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="block px-2 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
                >
                  {link.label}
                </Link>
              ))}
              <a href={headerContent.cta_link || 'tel:+902120000000'} className="block">
                <Button size="sm" className="w-full"><Phone className="h-4 w-4 mr-1.5" />{headerContent.cta_text || 'Bizi Arayin'}</Button>
              </a>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-300 py-12" data-testid="public-footer">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                {branding.logo ? (
                  <img src={`${BACKEND}${branding.logo}`} alt="" className="h-6 w-auto object-contain brightness-0 invert" />
                ) : (
                  <Wrench className="h-5 w-5 text-blue-400" />
                )}
                <span className="font-semibold text-white">{branding.site_title || footerContent.company_name || 'Uretim Noktasi'}</span>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed">
                Turkiye genelinde endustriyel uretim hizmetleri icin guvenilir teklif platformu.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-3">Hizmetler</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/hizmetler" className="hover:text-white">Tum Hizmetler</Link></li>
                <li><Link to="/teklif-al" className="hover:text-white">Teklif Al</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-3">Bilgi</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/hakkimizda" className="hover:text-white">Hakkimizda</Link></li>
                <li><Link to="/blog" className="hover:text-white">Blog</Link></li>
                <li><Link to="/iletisim" className="hover:text-white">Iletisim</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-3">Yasal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/kvkk" className="hover:text-white">KVKK</Link></li>
                <li><Link to="/aydinlatma-metni" className="hover:text-white">Aydinlatma Metni</Link></li>
                <li><Link to="/cerez-politikasi" className="hover:text-white">Cerez Politikasi</Link></li>
                <li><Link to="/sorumluluk-metni" className="hover:text-white">Sorumluluk Metni</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-slate-800 text-sm text-slate-500 text-center">
            {new Date().getFullYear()} {footerContent.company_name || 'Uretim Noktasi'}. Tum haklari saklidir.
          </div>
        </div>
      </footer>
    </div>
  );
}
